import { useState } from 'react'
import { Logo } from '../branding/Logo'
import { useAuth } from '../../lib/auth'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'

/**
 * Recovery screen — shown when Supabase fires `PASSWORD_RECOVERY`
 * (i.e. the user clicked the reset link in the email and we now have
 * a temporary session that lets them call `updateUser({ password })`).
 */
export function ResetPasswordScreen() {
  const t = useT()
  const { updatePassword, signOut } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = password.length >= 6 && password === confirm

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !ready) return
    setBusy(true); setError(null)
    try {
      await updatePassword(password)
      // After a successful reset Supabase keeps the user signed in.
      // The router moves to the hub via the normal flow.
    } catch (err) {
      setError(readError(err))
      setBusy(false)
    }
  }

  return (
    <div className="screen px-5 justify-center">
      <div className="max-w-sm mx-auto w-full space-y-6">
        <div className="flex flex-col items-center text-center">
          <Logo size={72} className="mb-4" />
          <h1 className="font-display text-3xl font-bold text-brand-text uppercase tracking-wide">
            {t('Set a new password')}
          </h1>
          <p className="text-brand-muted mt-1 text-sm">
            {t('Pick something memorable. Your browser will save it.')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-brand-text">{t('New password')}</span>
            <input
              type="password"
              name="new-password"
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t('At least 6 characters')}
              required
              minLength={6}
              autoFocus
              className="mt-1 w-full px-4 py-3 rounded-xl border border-brand-border
                         bg-white text-brand-text placeholder:text-brand-muted
                         focus:outline-hidden focus:border-brand-lime-dark focus:ring-2
                         focus:ring-brand-lime/30 transition-all"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-brand-text">{t('Confirm password')}</span>
            <input
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder={t('Type it again')}
              required
              minLength={6}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-brand-border
                         bg-white text-brand-text placeholder:text-brand-muted
                         focus:outline-hidden focus:border-brand-lime-dark focus:ring-2
                         focus:ring-brand-lime/30 transition-all"
            />
          </label>

          {confirm && confirm !== password && (
            <p className="text-sm text-brand-danger">{t('Passwords don’t match.')}</p>
          )}
          {error && <p className="text-sm text-brand-danger">{error}</p>}

          <button type="submit" className="btn-primary" disabled={busy || !ready}>
            {busy ? 'Updating…' : t('Update password')}
          </button>
        </form>

        <button
          type="button"
          onClick={() => signOut()}
          className="btn-ghost w-full"
        >
          {t('Cancel and sign out')}
        </button>
      </div>
    </div>
  )
}
